import { useEffect, useRef, useState } from 'react';
import { usePixelRatio } from '../hooks/usePixelRatio';
import { CANVAS, NEUTRAL } from '../theme';
import {
  describeBucket,
  formatAxis,
  formatTimeTick,
  indexAtX,
  niceMax,
  niceMaxRate,
  pickAt,
  type TrendSeries,
  tickIndexes,
  xOfIndex,
  yOfValue,
} from './trend';

/**
 * 시간대별 추이 그래프 (22-B회차, 빠진 정보 1·5).
 *
 * 캔버스 하나에 계열을 위아래로 쌓는다. 요청 수는 막대, 응답시간·비율은 선이다.
 * 세로축을 계열마다 따로 두는 이유는 단위가 다르기 때문이다 — 건수·ms·%를 한 축에 겹치면
 * 어느 눈금이 무엇인지 알 수 없다. X-View처럼 픽셀 버퍼까지 갈 이유는 없다. 점이 많아야
 * 2,000개라 도형 호출로도 한 프레임에 넉넉하다.
 *
 * 마우스를 올리면 그 구간의 값을 아래 한 줄로 낸다. 캔버스 안에 말풍선을 그리지 않는다 —
 * 글자가 겹치고 보조기술이 읽지 못한다.
 *
 * 23-A회차에 **구간을 누르면 그 시간대의 요청 목록으로** 간다(UI 피드백 17). 어느 계열을
 * 눌렀는지도 함께 올린다 — 오류율 선을 눌렀으면 부르는 쪽이 상태=오류까지 걸어 준다.
 * 캔버스는 키보드로 짚을 수 없으므로 부르는 쪽이 같은 자리에 링크를 하나 둔다.
 */

/** 계열 하나의 높이(CSS px)와 사이 간격 */
const ROW_HEIGHT = 72;
const ROW_GAP = 18;
const AXIS_LEFT = 52;
const AXIS_BOTTOM = 22;
const FONT = '11px -apple-system, "Segoe UI", "Apple SD Gothic Neo", "Malgun Gothic", sans-serif';

function chartHeight(seriesCount: number): number {
  return seriesCount * ROW_HEIGHT + Math.max(0, seriesCount - 1) * ROW_GAP + AXIS_BOTTOM + 8;
}

export function TrendChart({
  buckets,
  bucketMs,
  series,
  label,
  onPick,
}: {
  /** 구간 시작 시각. 시각 오름차순 */
  buckets: readonly number[];
  bucketMs: number;
  series: readonly TrendSeries[];
  /** 보조기술이 읽는 이름 */
  label: string;
  /** 구간을 눌렀을 때. 구간 시작 시각과 누른 계열 번호를 준다 (23-A회차) */
  onPick?: (pick: { bucketStart: number; series: number }) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [hover, setHover] = useState<number>();
  const [width, setWidth] = useState(0);
  // 배율이 다른 모니터로 옮기면 크기가 같아도 다시 그린다 (44회차)
  const ratio = usePixelRatio();
  const height = chartHeight(series.length);
  const spanMs = buckets.length > 0 ? bucketMs * buckets.length : 0;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // 크기 감시가 없는 환경(렌더링 테스트)에서는 처음 폭으로만 그린다
    if (typeof ResizeObserver === 'undefined') {
      setWidth(canvas.getBoundingClientRect().width);
      return;
    }
    const observer = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect;
      if (box) setWidth(box.width);
    });
    observer.observe(canvas);
    setWidth(canvas.getBoundingClientRect().width);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || width === 0) return;
    const context = canvas.getContext('2d');
    if (!context) return;
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    draw(context, { width, height, buckets, bucketMs, series, hover });
  }, [width, height, buckets, bucketMs, series, hover, ratio]);

  const plotWidth = Math.max(0, width - AXIS_LEFT - 8);

  const onMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    setHover(indexAtX(event.clientX - bounds.left - AXIS_LEFT, buckets.length, plotWidth));
  };

  const onClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (onPick === undefined) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const pick = pickAt(
      event.clientX - bounds.left - AXIS_LEFT,
      event.clientY - bounds.top,
      buckets.length,
      plotWidth,
      { rowHeight: ROW_HEIGHT, rowGap: ROW_GAP, seriesCount: series.length },
    );
    const at = pick === undefined ? undefined : buckets[pick.index];
    if (pick !== undefined && at !== undefined) {
      onPick({ bucketStart: at, series: pick.series });
    }
  };

  const readout =
    hover === undefined || buckets[hover] === undefined
      ? '그래프 위에 마우스를 올리면 그 구간의 값이 여기 나온다'
      : describeBucket(buckets[hover] as number, spanMs, series, hover);

  return (
    <div>
      <canvas
        ref={canvasRef}
        role="img"
        aria-label={label}
        onPointerMove={onMove}
        onPointerLeave={() => setHover(undefined)}
        onClick={onClick}
        className={`block w-full touch-none select-none ${onPick ? 'cursor-pointer' : ''}`}
        style={{ height }}
      />
      <p className="tabular mt-2 text-xs text-muted" aria-live="polite">
        {readout}
        {onPick !== undefined && hover !== undefined ? ' · 누르면 이 구간의 요청 목록' : ''}
      </p>
    </div>
  );
}

interface DrawInput {
  width: number;
  height: number;
  buckets: readonly number[];
  bucketMs: number;
  series: readonly TrendSeries[];
  hover?: number;
}

function draw(context: CanvasRenderingContext2D, input: DrawInput): void {
  const { width, height, buckets, bucketMs, series, hover } = input;
  const count = buckets.length;
  const plotLeft = AXIS_LEFT;
  const plotWidth = Math.max(0, width - AXIS_LEFT - 8);
  const spanMs = bucketMs * count;

  context.fillStyle = CANVAS.background;
  context.fillRect(0, 0, width, height);
  context.font = FONT;

  series.forEach((item, row) => {
    const top = row * (ROW_HEIGHT + ROW_GAP);
    const bottom = top + ROW_HEIGHT;
    const max = item.unit === 'pct' ? niceMaxRate(item.values) : niceMax(item.values);

    // 바닥선과 중간 격자
    context.strokeStyle = CANVAS.grid;
    context.lineWidth = 1;
    context.beginPath();
    context.moveTo(plotLeft, bottom + 0.5);
    context.lineTo(plotLeft + plotWidth, bottom + 0.5);
    context.moveTo(plotLeft, top + ROW_HEIGHT / 2 + 0.5);
    context.lineTo(plotLeft + plotWidth, top + ROW_HEIGHT / 2 + 0.5);
    context.stroke();

    // 세로 눈금: 위·중간
    context.fillStyle = CANVAS.tick;
    context.textAlign = 'right';
    context.textBaseline = 'middle';
    context.fillText(formatAxis(max, item.unit), plotLeft - 6, top + 6);
    context.fillText(formatAxis(max / 2, item.unit), plotLeft - 6, top + ROW_HEIGHT / 2);

    // 계열 이름은 왼쪽 위
    context.textAlign = 'left';
    context.textBaseline = 'top';
    context.fillStyle = item.color;
    context.fillText(item.label, plotLeft + 4, top + 2);

    if (count === 0 || plotWidth === 0) return;
    const slot = plotWidth / count;

    if (item.shape === 'bar') {
      context.fillStyle = item.color;
      for (let index = 0; index < count; index += 1) {
        const value = item.values[index];
        if (value === undefined) continue;
        const x = plotLeft + xOfIndex(index, count, plotWidth);
        const y = top + yOfValue(value, max, ROW_HEIGHT);
        const barWidth = Math.max(1, slot - 1);
        context.fillRect(x, y, barWidth, Math.max(1, bottom - y));
      }
    } else {
      context.strokeStyle = item.color;
      context.lineWidth = 1.5;
      context.beginPath();
      let open = false;
      for (let index = 0; index < count; index += 1) {
        const value = item.values[index];
        if (value === undefined) {
          open = false;
          continue;
        }
        const x = plotLeft + xOfIndex(index, count, plotWidth) + slot / 2;
        const y = top + yOfValue(value, max, ROW_HEIGHT);
        if (open) context.lineTo(x, y);
        else context.moveTo(x, y);
        open = true;
      }
      context.stroke();
      // 구간이 하나뿐이거나 이웃이 비어 선이 안 생기는 자리는 점으로
      context.fillStyle = item.color;
      for (let index = 0; index < count; index += 1) {
        const value = item.values[index];
        if (value === undefined) continue;
        const lonely = item.values[index - 1] === undefined && item.values[index + 1] === undefined;
        if (!lonely) continue;
        const x = plotLeft + xOfIndex(index, count, plotWidth) + slot / 2;
        const y = top + yOfValue(value, max, ROW_HEIGHT);
        context.fillRect(x - 1.5, y - 1.5, 3, 3);
      }
    }
  });

  // 가로 눈금은 맨 아래 한 줄
  const axisTop = series.length * (ROW_HEIGHT + ROW_GAP) - ROW_GAP + 6;
  context.fillStyle = CANVAS.tick;
  context.textAlign = 'center';
  context.textBaseline = 'top';
  for (const index of tickIndexes(count)) {
    const at = buckets[index];
    if (at === undefined) continue;
    const x = plotLeft + xOfIndex(index, count, plotWidth);
    context.fillText(formatTimeTick(at, spanMs), x + plotWidth / count / 2, axisTop);
  }

  // 마우스가 올라간 구간
  if (hover !== undefined && count > 0) {
    context.fillStyle = CANVAS.selectionFill;
    context.fillRect(plotLeft + xOfIndex(hover, count, plotWidth), 0, plotWidth / count, axisTop);
    context.strokeStyle = NEUTRAL.line;
    context.beginPath();
    const x = plotLeft + xOfIndex(hover, count, plotWidth) + 0.5;
    context.moveTo(x, 0);
    context.lineTo(x, axisTop);
    context.stroke();
  }
}
